// src/pages/TestScoreCommentsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2 } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";

import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

export default function TestScoreCommentsTable() {
  const { testId, categoryId } = useParams();

  const [rows, setRows] = useState([]);
  const [testName, setTestName] = useState("");
  const [categoryName, setCategoryName] = useState("");

  /* i18n */
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  const token = localStorage.getItem("admin_token");
  const base  = process.env.REACT_APP_API_BASE_URL;

  /* ---------------- helpers ---------------- */
  const pickName = (obj = {}) =>
    obj.name ??
    obj.title ??
    obj.category_name ??
    obj.label ??
    "";

  const normalizeArray = (data) =>
    Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.rows)
          ? data.rows
          : [];

  /* ---------------- fetch test & category names ---------------- */
  useEffect(() => {
    (async () => {
      try {
        // Test adı
        try {
          const { data: test } = await axios.get(`${base}/api/tests/${testId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setTestName(pickName(test) || `Test #${testId}`);
        } catch {
          setTestName(`Test #${testId}`);
        }

        // Kategori adı (varsa)
        if (!categoryId) {
          setCategoryName("");
          return;
        }

        let name = "";

        // 1) /api/categories/:id
        try {
          const { data } = await axios.get(`${base}/api/categories/${categoryId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          name = pickName(data) || name;
        } catch {}

        // 2) /api/test-categories/:id
        if (!name) {
          try {
            const { data } = await axios.get(`${base}/api/test-categories/${categoryId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            name = pickName(data) || name;
          } catch {}
        }

        // 3) /api/tests/:testId/categories (liste içinde id eşle)
        if (!name) {
          try {
            const { data } = await axios.get(`${base}/api/tests/${testId}/categories`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const list = normalizeArray(data);
            const found = list.find(
              (c) => String(c.id) === String(categoryId) || String(c.category_id) === String(categoryId)
            );
            name = pickName(found) || name;
          } catch {}
        }

        setCategoryName(name || `Kategori #${categoryId}`);
      } catch {
        // sessiz geç
      }
    })();
  }, [testId, categoryId]);

  /* ---------------- fetch rows ---------------- */
  const fetchData = async () => {
    try {
      const url = categoryId
        ? `${base}/api/score-comments/tests/${testId}/category/${categoryId}/score-comments`
        : `${base}/api/score-comments/tests/${testId}`;

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRows(normalizeArray(data));
    } catch (err) {
      console.error("❌ Puan açıklamaları alınamadı:", err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, categoryId]);

  /* ---------------- delete ---------------- */
  const handleDelete = (commentId) =>
    confirmAlert({
      title: t("CONFIRM_DELETE", "Silmek istediğinize emin misiniz?"),
      message: t("DELETE_WARNING", "Bu işlem geri alınamaz."),
      buttons: [
        {
          label: t("YES", "Evet, sil"),
          onClick: async () => {
            try {
              await axios.delete(
                `${base}/api/score-comments/${commentId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(t("DELETE_SUCCESS", "Kayıt silindi"));
              fetchData();
            } catch (err) {
              console.error("❌ Silme hatası:", err);
              toast.error(t("DELETE_FAILED", "Silme işlemi başarısız"));
            }
          },
        },
        { label: t("CANCEL", "İptal"), onClick: () => {} },
      ],
    });

  /* ---------------- columns ---------------- */
  const basePath = categoryId
    ? `/test/score/${testId}/category/${categoryId}`
    : `/test/score/${testId}`;

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: () => "ID", size: 60 },
      { accessorKey: "min_score", header: () => t("TABLE_MIN_SCORE", "Min. Puan") },
      { accessorKey: "max_score", header: () => t("TABLE_MAX_SCORE", "Maks. Puan") },
      {
        accessorKey: "actions",
        header: () => t("ACTIONS", "İşlemler"),
        enableSorting: false,
        cell: (info) => {
          const { id } = info.row.original;
          return (
            <div className="hstack gap-2 justify-content-end">
              <Link
                to={`/test/score/${testId}/view/${id}`}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={t("VIEW", "Görüntüle")}
              >
                <FiEye />
              </Link>
              <Link
                to={`${basePath}/edit/${id}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={t("EDIT", "Düzenle")}
              >
                <FiEdit3 />
              </Link>
              <button
                type="button"
                onClick={() => handleDelete(id)}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={t("DELETE", "Sil")}
              >
                <FiTrash2 />
              </button>
            </div>
          );
        },
      },
    ],
    [translations, testId, basePath]
  );

  /* ---------------- header line ---------------- */
  const contextLine = categoryId ? (
    <>
      Test: <strong>{testName}</strong>
      {" · "}
      Kategori: <strong>{categoryName }</strong>
    </>
  ) : (
    <>
      Test: <strong>{testName }</strong>
    </>
  );

  return (
    <div>
      {/* Üst şerit: bağlam + toplam kayıt */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="small text-muted">{contextLine}</div>
        
      </div>

      <Table data={rows} columns={columns} />
    </div>
  );
}
